import { Box } from '@mantine/core';
import type { CustomPropertyOut } from '@repo/api-codegen';
import { FilterOptionType } from '@repo/common/components/Filter';
import { EntityType } from '@repo/common/enums/entityType';
import { Text as FoundationsText } from '@repo/foundations';
import type { IColumn } from '../../../../api';
import type {
	ExtendedDataTableColumn,
	Render,
} from '../../../../components/TableV2/types';

// Mapping from backend native_type values to user-friendly display names
export const SEMANTIC_VIEW_TYPE_DISPLAY_NAMES: Record<string, string> = {
	// Snowflake semantic views
	snowflake_semantic_metric: 'Metric',
	snowflake_semantic_dimension: 'Dimension',
	snowflake_semantic_fact: 'Fact',
	snowflake_semantic_filter: 'Filter',
	snowflake_semantic_time_dimension: 'Time Dimension',
	snowflake_semantic_relationship: 'Relationship',
	// dbt semantic models
	dbt_semantic_measure: 'Measure',
	dbt_semantic_dimension: 'Dimension',
	dbt_semantic_entity: 'Entity',
};

export const semanticViewTypeRender: Render<IColumn> = (record: IColumn) => {
	const displayName = record.native_type
		? SEMANTIC_VIEW_TYPE_DISPLAY_NAMES[record.native_type] || record.native_type
		: record.native_type;
	return <FoundationsText size="sm">{displayName}</FoundationsText>;
};

export const getSemanticViewColumns = (
	entityType: EntityType = EntityType.column,
	customProperties: CustomPropertyOut[] = []
): ExtendedDataTableColumn<IColumn>[] => {
	const expressionPropertyId = customProperties.find(
		(cp) => cp.name === 'expression'
	)?.id;
	const exprPropertyId = customProperties.find((cp) => cp.name === 'expr')?.id;

	return [
		{
			accessor: 'native_type',
			titleLabel: 'Type',
			width: 180,
			sortable: true,
			filtering: true,
			explicit: true,
			render: semanticViewTypeRender,
			filterOptionType:
				entityType === EntityType.column
					? FilterOptionType.SEMANTIC_VIEW_COLUMN_TYPE
					: FilterOptionType.SEMANTIC_VIEW_GENERIC_TYPE,
		},
		{
			accessor: 'expression',
			titleLabel: 'Expression',
			width: 250,
			sortable: false,
			filtering: false,
			explicit: true,
			render: (record: IColumn) => {
				const expressionVal =
					(expressionPropertyId &&
						record.properties2?.[expressionPropertyId]) ||
					(exprPropertyId && record.properties2?.[exprPropertyId]) ||
					record.properties2?.expression;

				const expression =
					typeof expressionVal === 'string' ? expressionVal : undefined;

				return expression ? (
					<Box component="code">{expression}</Box>
				) : (
					<FoundationsText color="text/secondary/default" size="sm">
						—
					</FoundationsText>
				);
			},
		},
	];
};
